/**
 * 自定义数字输入组件（CustomNumber）
 */

import type { ReactElement, ChangeEvent } from 'react'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import type { FormFieldProps } from './types'

/**
 * 自定义数字输入组件
 * @param props 组件属性
 * @returns 组件元素
 */
export function CustomNumberField({
  component,
  value,
  onChange,
  error,
  disabled = false,
}: FormFieldProps): ReactElement {
  const numValue = typeof value === 'number'
    ? value
    : typeof component.default_value === 'number'
      ? component.default_value
      : 0
  const config = component.config as { min?: number; max?: number; step?: number } | null
  const min = config?.min
  const max = config?.max
  const step = config?.step ?? 1

  /**
   * 处理输入变化
   * @param event 输入事件
   */
  function handleChange(event: ChangeEvent<HTMLInputElement>): void {
    const newValue = parseFloat(event.target.value)
    if (isNaN(newValue)) {
      return
    }
    // 限制在范围内
    let clampedValue = newValue
    if (min !== undefined && clampedValue < min) {
      clampedValue = min
    }
    if (max !== undefined && clampedValue > max) {
      clampedValue = max
    }
    onChange(clampedValue)
  }

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">
        {component.title}
        {component.is_required && (
          <span className="ml-1 text-destructive">*</span>
        )}
      </label>
      <Input
        type="number"
        value={numValue}
        onChange={handleChange}
        disabled={disabled}
        min={min}
        max={max}
        step={step}
        className={cn(
          'w-full',
          error !== undefined && 'border-destructive focus-visible:ring-destructive',
          disabled && 'cursor-not-allowed opacity-50'
        )}
      />
      {(min !== undefined || max !== undefined) && (
        <p className="text-xs text-muted-foreground">
          范围: {min ?? '-∞'} ~ {max ?? '+∞'}
        </p>
      )}
      {error !== undefined && (
        <p className="text-xs text-destructive">{error}</p>
      )}
    </div>
  )
}
